import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { HTML_LANG, enT, isLang, makeT, type Lang, type Translate } from "./translate";

const STORAGE_KEY = "blogic.apply.lang";

interface LangState {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translate;
  locale: string | undefined;
}

const LangContext = createContext<LangState>({
  lang: "en",
  setLang: () => {},
  t: enT,
  locale: undefined,
});

function readStoredLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLang(stored) ? stored : "en";
  } catch {
    return "en";
  }
}

function storeLang(lang: Lang) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    return;
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStoredLang);

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.lang;
    root.lang = HTML_LANG[lang];
    return () => {
      root.lang = previous;
    };
  }, [lang]);

  const value = useMemo<LangState>(() => ({
    lang,
    setLang: (next) => {
      setLangState(next);
      storeLang(next);
    },
    t: makeT(lang),
    locale: lang === "en" ? undefined : HTML_LANG[lang],
  }), [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangState {
  return useContext(LangContext);
}

export function useT(): Translate {
  return useContext(LangContext).t;
}
